import {
  defaultFairnessCopy,
  FAIRNESS_EXPLANATION,
  fairnessSeedFacts,
} from "@loot-os/api-contract";
import type { FairnessSeedContent } from "@loot-os/design-system/templates/fairness/fairnessContent";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { seedQueryOptions } from "../../../../lib/queries.js";
import { utcDayKey } from "../../../../lib/seedDate.js";

/**
 * One UTC day's server seed: `GET /v1/fairness/seeds/:date`. The day starts at today's (UTC) and
 * goes no later than tomorrow's, the furthest seed already committed. Looking up the same day
 * again refetches it.
 */
export function useFairnessSeedContent(): FairnessSeedContent {
  const copy = defaultFairnessCopy().seed;
  const [date, setDate] = useState(() => utcDayKey(new Date()));
  const [submittedDate, setSubmittedDate] = useState<string | null>(null);

  const query = useQuery({
    ...seedQueryOptions(submittedDate ?? "none"),
    enabled: !!submittedDate,
  });
  const busy = query.isFetching;
  const seed = query.data ?? null;
  const error =
    !busy && query.error
      ? query.error instanceof Error
        ? query.error.message
        : "lookup failed"
      : null;

  return {
    title: copy.title,
    description: FAIRNESS_EXPLANATION.seed,
    date: { ...copy.date, value: date, max: utcDayKey(new Date(), 1), onChange: setDate },
    submit: { ...copy.submit, busy, disabled: busy },
    onSubmit: () => {
      if (date === submittedDate) void query.refetch();
      else setSubmittedDate(date);
    },
    error,
    facts: seed ? fairnessSeedFacts(seed) : null,
  };
}
